import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { KeyRound, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api from "../services/api";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [show, setShow] = useState({ cur: false, n: false, c: false });
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    setShow({ cur: false, n: false, c: false });
  };

  const handleClose = (o) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!currentPassword) return toast.error("Enter your current password");
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    if (newPassword === currentPassword) return toast.error("New password must be different from current");
    if (newPassword !== confirmPassword) return toast.error("New password and confirmation don't match");
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      handleClose(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't change password");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] text-lg flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#FF6B00]" /> Change Password
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Use a strong password — at least 6 characters, ideally with letters, numbers, and a symbol.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {[
            { id: "cur", label: "Current password", val: currentPassword, set: setCurrentPassword, testid: "current-password-input" },
            { id: "n",   label: "New password",      val: newPassword,     set: setNewPassword,     testid: "new-password-input" },
            { id: "c",   label: "Confirm new password", val: confirmPassword, set: setConfirmPassword, testid: "confirm-password-input" },
          ].map((f) => (
            <div key={f.id}>
              <Label className="text-xs font-semibold text-slate-700">{f.label}</Label>
              <div className="relative mt-1">
                <Input
                  type={show[f.id] ? "text" : "password"}
                  value={f.val}
                  onChange={(e) => f.set(e.target.value)}
                  className="rounded-xl h-10 pr-10"
                  data-testid={f.testid}
                  autoComplete={f.id === "cur" ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShow((s) => ({ ...s, [f.id]: !s[f.id] }))}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700"
                  data-testid={`toggle-${f.testid}`}
                  tabIndex={-1}
                >
                  {show[f.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full bg-[#FF6B00] hover:bg-[#E65A00] rounded-xl h-11 mt-2"
            data-testid="submit-change-password-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
